import { Alert, Flex, Spin } from 'antd';
import { useEffect, useRef } from 'react';
import invariant from 'tiny-invariant';
import { useTranslation } from 'translations/hooks';

import { useAppContext } from 'business/contextProviders/useAppContext';
import { useFetchRingData } from 'business/quality-control/pages/record-segment-defect/hooks/use-fetch-ring-data';
import {
  DefectReportTypeDto,
  PositionItemDto,
  SegmentConfigItemDto,
} from 'generated/apiSchemas';
import { Board } from 'ui/quality-control/board';
import { HourLines } from 'ui/quality-control/board/hour-lines';
import { BoardDisplayMode } from 'ui/quality-control/board/types';
import {
  BoardViewMode,
  formatBoardSegments,
  getBoardConfiguration,
  getHourLineLabels,
} from 'ui/quality-control/board/utils';

import styles from './index.module.scss';

export const RingBoardViewer = ({
  defectId,
  ringNumber,
  segmentDrawConfigs,
  segmentWidth,
  tunnelPositions,
  defectReportTypes,
  isLoadingBoardConfig,
  boardConfigError,
}: {
  defectId?: string;
  ringNumber: number | null;
  segmentDrawConfigs: SegmentConfigItemDto[];
  segmentWidth: number;
  tunnelPositions: PositionItemDto[];
  defectReportTypes: DefectReportTypeDto[];
  isLoadingBoardConfig: boolean;
  boardConfigError: unknown;
}) => {
  const { t } = useTranslation();
  const { currentConstructionSiteId } = useAppContext();
  invariant(currentConstructionSiteId, 'No construction site id');

  const { fetchRingData, ringData, isLoading } = useFetchRingData(
    currentConstructionSiteId,
  );

  const lastFetchedRingRef = useRef<number | null>(null);

  useEffect(() => {
    if (ringNumber && ringNumber !== lastFetchedRingRef.current) {
      lastFetchedRingRef.current = ringNumber;
      fetchRingData(ringNumber);
    }
  }, [ringNumber, fetchRingData]);

  const defects = defectId
    ? (ringData?.defects.filter((defect) => defect.id === defectId) ?? [])
    : (ringData?.defects ?? []);

  const rings = formatBoardSegments(
    segmentDrawConfigs,
    tunnelPositions,
    ringData?.rings ?? [],
    defects,
    defectReportTypes,
  );

  const boardConfiguration = getBoardConfiguration(
    segmentDrawConfigs ?? [],
    segmentWidth,
    rings.length,
    BoardDisplayMode.Ring,
  );

  if (boardConfigError) {
    return <Alert message={t('errors.error_generic')} type="error" />;
  }

  if (isLoading || isLoadingBoardConfig) {
    return (
      <Flex justify="center" align="center" style={{ padding: '40px 0' }}>
        <Spin tip={t('common.loading')}>
          <div style={{ minHeight: 60, minWidth: 60 }} />
        </Spin>
      </Flex>
    );
  }

  if (
    segmentDrawConfigs &&
    segmentDrawConfigs.length > 0 &&
    rings.length > 0 &&
    !!boardConfiguration
  ) {
    return (
      <div className={styles.svgContainer}>
        <svg
          viewBox={`0 0 ${boardConfiguration.board.width} ${boardConfiguration.board.height}`}
          preserveAspectRatio="xMidYMin meet"
          className={styles.boardSvg}
        >
          <g
            transform={`scale(${1.1})`}
            style={{
              transformOrigin: 'top left',
              transition: 'transform 0.2s ease-out',
            }}
          >
            <Board boardConfiguration={boardConfiguration} rings={rings} />
            <HourLines
              labels={getHourLineLabels(BoardViewMode.Entry)}
              totalSegmentWidth={boardConfiguration.ringSegmentsWidth}
              marginX={boardConfiguration.margin.leftHourLines}
              boardHeight={boardConfiguration.board.height}
            />
          </g>
        </svg>
      </div>
    );
  }

  return null;
};
